import type { Prompt, ContextItem } from "@/context/prompt"
import type { PromptHistoryEntry } from "@/components/prompt-input/history"
import { indexedDBSource } from "./browser"
import { attachmentKeys, createPromptAttachmentCodec, decodePrompt, type StoredAttachment, type StoredPrompt } from "./prompt-attachments"

export type PromptDraft = { prompt: Prompt; context: { items: (ContextItem & { key: string })[] } }
export type PromptHistoryMode = "normal" | "shell"
type DraftRow = { key: string; version: string; prompt: StoredPrompt; context: PromptDraft["context"] }
type HistoryRow = { key: string; mode: PromptHistoryMode; prompt: StoredPrompt; comments: PromptHistoryEntry["comments"]; bytes: number; time: number }
const DATABASE = "mux-prompts"
const VERSION = 2
const STORES = ["drafts", "history", "attachments"]
// History is disposable recall, not the server transcript. Keep at most 100
// entries per mode and 32 MiB of accounted payloads across both histories.
const HISTORY_BYTES = 32 * 1024 * 1024
const HISTORY_ENTRIES = 100

export class PromptStorageError extends Error {}

function result<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("Prompt storage request failed."))
  })
}
function completed(tx: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error ?? new Error("Prompt storage transaction was aborted."))
    tx.onerror = () => reject(tx.error ?? new Error("Prompt storage transaction failed."))
  })
}

/** Local unsent drafts and disposable input history; never authoritative chat state. */
export function createPromptRepository(options: { indexedDB?: IDBFactory; historyBytes?: number; historyEntries?: number } = {}) {
  let opening: Promise<IDBDatabase> | undefined
  let closed = false
  const codec = createPromptAttachmentCodec()
  let tail: Promise<unknown> = Promise.resolve()
  const database = () => {
    if (closed) return Promise.reject(new Error("Prompt storage is closed."))
    if (opening) return opening
    opening = new Promise<IDBDatabase>((resolve, reject) => {
      const source = options.indexedDB ?? indexedDBSource()
      if (!source) throw new Error("Browser storage is unavailable.")
      const request = source.open(DATABASE, VERSION)
      let failed = false
      request.onupgradeneeded = () => {
        const db = request.result
        for (const name of STORES) if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: "key" })
        for (const name of Array.from(db.objectStoreNames)) if (!STORES.includes(name)) db.deleteObjectStore(name)
      }
      request.onsuccess = () => {
        if (failed || closed) { request.result.close(); return }
        request.result.onversionchange = () => { request.result.close(); opening = undefined }
        resolve(request.result)
      }
      request.onerror = () => reject(request.error ?? new Error("Could not open prompt storage."))
      request.onblocked = () => { failed = true; reject(new Error("Close older Kettle tabs and retry saving.")) }
    }).catch((error) => { opening = undefined; throw error })
    return opening
  }
  const serial = <T>(work: () => Promise<T>): Promise<T> => {
    const next = tail.then(work)
    tail = next.catch(() => undefined)
    return next
  }
  const transaction = async <T>(mode: IDBTransactionMode, work: (tx: IDBTransaction) => Promise<T>) => {
    const db = await database()
    const tx = db.transaction(STORES, mode)
    const done = completed(tx)
    try {
      const value = await work(tx)
      await done
      return value
    } catch (error) {
      try { tx.abort() } catch { /* Already aborted or complete. */ }
      await done.catch(() => undefined)
      throw error
    }
  }
  const references = async (tx: IDBTransaction, owner: string, previous: StoredPrompt, next: StoredPrompt, blobs = new Map<string, Blob>()) => {
    const store = tx.objectStore("attachments")
    const nextKeys = new Set(attachmentKeys(next))
    for (const key of new Set([...attachmentKeys(previous), ...nextKeys])) {
      const existing: StoredAttachment | undefined = await result(store.get(key))
      const owners = new Set(existing?.references ?? [])
      owners.delete(owner)
      if (nextKeys.has(key)) owners.add(owner)
      if (!owners.size) { store.delete(key); continue }
      const blob = existing?.blob ?? blobs.get(key)
      if (!blob) throw new Error("An image attachment could not be recovered.")
      store.put({ key, blob, references: [...owners] } satisfies StoredAttachment)
    }
  }
  const hydrate = async (tx: IDBTransaction, prompt: StoredPrompt) => {
    const blobs = new Map<string, Blob>()
    for (const key of attachmentKeys(prompt)) {
      const row: StoredAttachment | undefined = await result(tx.objectStore("attachments").get(key))
      if (!row) throw new Error("A saved image attachment is missing.")
      blobs.set(key, row.blob)
    }
    return { prompt, blobs }
  }
  const prune = async (tx: IDBTransaction) => {
    const store = tx.objectStore("history")
    const rows = (await result<HistoryRow[]>(store.getAll())).sort((a, b) => b.time - a.time || b.key.localeCompare(a.key))
    let bytes = 0
    const counts = { normal: 0, shell: 0 }
    for (const row of rows) {
      if (bytes + row.bytes <= (options.historyBytes ?? HISTORY_BYTES) && counts[row.mode] < (options.historyEntries ?? HISTORY_ENTRIES)) {
        bytes += row.bytes
        counts[row.mode]++
        continue
      }
      store.delete(row.key)
      await references(tx, `history:${row.key}`, row.prompt, [])
    }
  }
  const writeDraft = (key: string, version: string, draft: PromptDraft) => serial(async () => {
    const encoded = await codec.encode(draft.prompt)
    await transaction("readwrite", async (tx) => {
      const store = tx.objectStore("drafts")
      const old: DraftRow | undefined = await result(store.get(key))
      await references(tx, `draft:${key}`, old?.prompt ?? [], encoded.prompt, encoded.blobs)
      store.put({ key, version, prompt: encoded.prompt, context: draft.context } satisfies DraftRow)
    })
  })
  const readDraft = (key: string) => serial(async () => {
    const saved = await transaction("readonly", async (tx) => {
      const row: DraftRow | undefined = await result(tx.objectStore("drafts").get(key))
      return row ? { row, encoded: await hydrate(tx, row.prompt) } : undefined
    })
    if (!saved) return
    return { version: saved.row.version, draft: { prompt: await decodePrompt(saved.encoded.prompt, saved.encoded.blobs), context: saved.row.context } }
  })
  const readHistory = (mode: PromptHistoryMode) => serial(async () => {
    const saved = await transaction("readonly", async (tx) => {
      const rows = (await result<HistoryRow[]>(tx.objectStore("history").getAll()))
        .filter((row) => row.mode === mode).sort((a, b) => b.time - a.time || b.key.localeCompare(a.key))
      return Promise.all(rows.map(async (row) => ({ row, encoded: await hydrate(tx, row.prompt) })))
    })
    return Promise.all(saved.map(async ({ row, encoded }) => ({ prompt: await decodePrompt(encoded.prompt, encoded.blobs), comments: row.comments })))
  })

  /** Records an accepted submission; resolves true when no draft remains stored under `key`. */
  const accept = (input: { key: string; version: string; submissionID: string; entry?: PromptHistoryEntry; mode: PromptHistoryMode; time: number }) => serial(async () => {
    const encoded = input.entry ? await codec.encode(input.entry.prompt) : undefined
    if (encoded) encoded.bytes += new TextEncoder().encode(JSON.stringify(input.entry!.comments)).byteLength
    if (encoded && encoded.bytes > (options.historyBytes ?? HISTORY_BYTES)) throw new PromptStorageError("This prompt exceeds the 32 MiB input-history budget. Its saved draft was kept; the submitted message is unaffected.")
    return transaction("readwrite", async (tx) => {
      // Insert history before removing draft references: the blob is never duplicated.
      if (encoded) {
        const store = tx.objectStore("history")
        const old: HistoryRow | undefined = await result(store.get(input.submissionID))
        await references(tx, `history:${input.submissionID}`, old?.prompt ?? [], encoded.prompt, encoded.blobs)
        store.put({ key: input.submissionID, mode: input.mode, prompt: encoded.prompt, comments: input.entry!.comments, bytes: encoded.bytes, time: input.time } satisfies HistoryRow)
      }
      const drafts = tx.objectStore("drafts")
      const draft: DraftRow | undefined = await result(drafts.get(input.key))
      // A response from an older submission must not clear a newer draft, including in another tab.
      const removed = draft?.version === input.version
      if (removed) {
        drafts.delete(input.key)
        await references(tx, `draft:${input.key}`, draft.prompt, [])
      }
      await prune(tx)
      return removed || !draft
    })
  })

  const removeDrafts = (prefix: string) => serial(() => transaction("readwrite", async (tx) => {
    const drafts = tx.objectStore("drafts")
    const rows = await result<DraftRow[]>(drafts.getAll(IDBKeyRange.bound(prefix, `${prefix}\uffff`)))
    for (const row of rows) {
      await references(tx, `draft:${row.key}`, row.prompt, [])
      drafts.delete(row.key)
    }
  }))

  return {
    readDraft, writeDraft, readHistory, accept, removeDrafts,
    settled: () => tail,
    dispose() {
      void tail.then(async () => { closed = true; codec.clear(); (await opening?.catch(() => undefined))?.close() })
    },
  }
}
export type PromptRepository = ReturnType<typeof createPromptRepository>
